import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../services/supabase'
import mixpanelService from '../services/mixpanel'
import { CHEMICAL_ADDITIONS_SELECT } from '../utils/constants'

const CATEGORY_MAPPING = {
  'liquid_chlorine': 'sanitizer',
  'liquid_chlorine_10': 'sanitizer',
  'liquid_chlorine_12': 'sanitizer',
  'liquid_chlorine_12_5': 'sanitizer',
  'household_bleach_6': 'sanitizer',
  'cal_hypo_65': 'sanitizer',
  'cal_hypo_73': 'sanitizer',
  'trichlor': 'sanitizer',
  'dichlor': 'sanitizer',
  'soda_ash': 'ph_adjuster',
  'borax': 'ph_adjuster',
  'muriatic_acid': 'ph_adjuster',
  'baking_soda': 'alkalinity',
  'dry_stabilizer': 'cya',
  'liquid_stabilizer': 'cya',
  'calcium_chloride': 'calcium'
}

export const useChemicalAdditions = (poolId) => {
  const [chemicalAdditions, setChemicalAdditions] = useState([])
  const [chemicalTypes, setChemicalTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Fetch chemical types (reference data)
  const fetchChemicalTypes = useCallback(async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('chemical_types')
        .select('*')
        .order('category', { ascending: true })

      if (fetchError) throw fetchError
      setChemicalTypes(data || [])
    } catch (err) {
      console.error('Error fetching chemical types:', err)
      setError(err.message || 'Failed to load chemical types')
    }
  }, [])

  // Fetch chemical additions for a pool
  const fetchChemicalAdditions = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      if (!poolId) {
        setChemicalAdditions([])
        return
      }

      const { data, error } = await supabase
        .from('chemical_additions')
        .select(CHEMICAL_ADDITIONS_SELECT)
        .eq('pool_id', poolId)
        .order('added_at', { ascending: false })
        .limit(100)

      if (error) throw error
      setChemicalAdditions(data || [])
    } catch (error) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }, [poolId])

  // Map chemical keys to database categories
  const getChemicalCategory = (chemicalKey) => {
    return CATEGORY_MAPPING[chemicalKey] || 'sanitizer'
  }

  // Create a new chemical addition
  const createChemicalAddition = async (additionData) => {
    try {
      // Find the chemical type ID based on the chemical name/key
      let chemicalTypeId = null
      
      if (additionData.chemical) {
        // Try to find chemical type by matching name or key
        const chemicalType = chemicalTypes.find(ct => 
          ct.name.toLowerCase().includes(additionData.chemical.toLowerCase()) ||
          additionData.chemical.toLowerCase().includes(ct.name.toLowerCase()) ||
          ct.name.toLowerCase().replace(/[^a-z0-9]/g, '') === additionData.chemicalName?.toLowerCase().replace(/[^a-z0-9]/g, '')
        )
        
        if (chemicalType) {
          chemicalTypeId = chemicalType.id
        }
        
        // If no match found, try to create a new entry with proper category
        if (!chemicalTypeId) {
          const category = getChemicalCategory(additionData.chemical)
          try {
            const { data: newChemicalType, error: createError } = await supabase
              .from('chemical_types')
              .insert([{
                name: additionData.chemicalName || additionData.chemical,
                category: category,
                default_unit: additionData.unit
              }])
              .select()
              .single()
            
            if (createError) {
              // If we can't create a new chemical type, continue without it
              console.warn('Cannot create new chemical type:', createError)
              chemicalTypeId = null
            } else {
              chemicalTypeId = newChemicalType.id
              // Update local chemical types
              setChemicalTypes(prev => [...prev, newChemicalType])
            }
          } catch (error) {
            console.warn('Cannot create new chemical type:', error)
            chemicalTypeId = null
          }
        }
      }

      const { data, error } = await supabase
        .from('chemical_additions')
        .insert([
          {
            pool_id: poolId,
            chemical_type_id: chemicalTypeId,
            amount: additionData.amount,
            unit: additionData.unit,
            added_at: additionData.added_at,
            notes: additionData.notes || null,
            measurement_id: additionData.measurementId || null
          },
        ])
        .select(CHEMICAL_ADDITIONS_SELECT)

      if (error) throw error
      const created = data?.[0]

      // Update local state
      if (created) {
        setChemicalAdditions([created, ...chemicalAdditions])
      }

      // Track chemical addition (isolated so analytics failures don't affect the result)
      try {
        mixpanelService.trackFunnelStep('add_chemical', {
          addition_id: created?.id,
          pool_id: poolId,
          chemical_type: additionData.chemicalName || additionData.chemical,
          amount: additionData.amount,
          unit: additionData.unit,
          has_notes: !!additionData.notes,
          from_recommendation: !!additionData.recommendation
        })
      } catch (trackingError) {
        console.warn('Mixpanel tracking failed:', trackingError)
      }

      return { data: created, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }

  // Update a chemical addition
  const updateChemicalAddition = async (additionId, updateData) => {
    try {
      const { data, error } = await supabase
        .from('chemical_additions')
        .update({
          amount: updateData.amount,
          unit: updateData.unit,
          added_at: updateData.added_at,
          notes: updateData.notes || null
        })
        .eq('id', additionId)
        .eq('pool_id', poolId)
        .select(CHEMICAL_ADDITIONS_SELECT)

      if (error) throw error
      const updated = data?.[0]

      // Update local state
      if (updated) {
        setChemicalAdditions(chemicalAdditions.map(ca =>
          ca.id === additionId ? updated : ca
        ))
      }

      return { data: updated, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }

  // Delete a chemical addition
  const deleteChemicalAddition = async (additionId) => {
    try {
      const { error } = await supabase
        .from('chemical_additions')
        .delete()
        .eq('id', additionId)
        .eq('pool_id', poolId)

      if (error) throw error
      
      // Update local state
      setChemicalAdditions(chemicalAdditions.filter(ca => ca.id !== additionId))
      
      return { error: null }
    } catch (error) {
      return { error }
    }
  }

  // Get recent chemical additions
  const getRecentAdditions = (limit = 10) => {
    return chemicalAdditions.slice(0, limit)
  }

  // Get additions for a specific date range
  const getAdditionsInRange = (startDate, endDate) => {
    return chemicalAdditions.filter(addition => {
      const addedAt = new Date(addition.added_at)
      return addedAt >= startDate && addedAt <= endDate
    })
  }

  // Get additions by chemical type
  const getAdditionsByChemical = (chemicalName) => {
    return chemicalAdditions.filter(addition => 
      addition.chemical_types?.name.toLowerCase().includes(chemicalName.toLowerCase())
    )
  }

  // Calculate total amount added for a chemical type in a time period
  const getTotalAmountAdded = (chemicalName, days = 30) => {
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - days)
    
    return chemicalAdditions
      .filter(addition => {
        const addedAt = new Date(addition.added_at)
        return addedAt >= cutoffDate && 
               addition.chemical_types?.name.toLowerCase().includes(chemicalName.toLowerCase())
      })
      .reduce((total, addition) => total + addition.amount, 0)
  }

  // Initialize data on mount
  useEffect(() => {
    fetchChemicalTypes()
  }, [fetchChemicalTypes])

  useEffect(() => {
    fetchChemicalAdditions()
  }, [poolId, fetchChemicalAdditions])

  return {
    chemicalAdditions,
    chemicalTypes,
    loading,
    error,
    createChemicalAddition,
    updateChemicalAddition,
    deleteChemicalAddition,
    getRecentAdditions,
    getAdditionsInRange,
    getAdditionsByChemical,
    getTotalAmountAdded,
    refetch: fetchChemicalAdditions,
  }
}